import ReactGA4 from "react-ga4";
import i18next from "i18next";
import { isTutorialActive } from "../components/tutorial/tutorialInterceptor"; // GUNDI-5665, temporary
import { getMyDetails } from "../api/users/usersApi";
import { loadAllOrganizations } from "../api/organizations/loadOrganizations";

// GUNDI-5665 (TEMPORARY, ≤3 weeks): while the guided tour runs, pageviews and
// events are DROPPED — the tour navigates mock pages and clicks real controls
// on the user's behalf, and those synthetic hits would pollute real usage
// metrics (page_view for /routes/tutorial-*, and a destination_added for the
// tour's fake attach — the same event the self-service funnel counts). Only
// send/event are gated: gtag carries once-per-session user properties, which
// hold no tour data and must not be silently skipped if their one send happens
// to land mid-tour. Remove this shim with the tutorial (see the removal
// manifest in tutorialInterceptor.ts) by renaming ReactGA4 back to ReactGA.
const ReactGA = {
    send: (payload: Parameters<typeof ReactGA4.send>[0]) => {
        if (!isTutorialActive()) ReactGA4.send(payload);
    },
    event: (name: string, params?: Record<string, any>) => {
        if (!isTutorialActive()) ReactGA4.event(name, params);
    },
    gtag: (...args: Parameters<typeof ReactGA4.gtag>) => ReactGA4.gtag(...args),
};

// ── Note on environment separation ──
// There are 2 GA4 Measurement IDs to separate production from the rest of environments (dev/stage)
// Events fire in all environments but land in separate GA properties.

// ── Internal domain list ──

const INTERNAL_DOMAINS = ["earthranger.com"];

export function getUserType(email: string): "internal" | "external" {
    const domain = email.split("@")[1]?.toLowerCase();
    return INTERNAL_DOMAINS.includes(domain) ? "internal" : "external";
}

// ── Page title resolver ──
// Maps SPA routes to stable snake_case titles for GA. Falls back to the
// pathname itself so every event always carries a non-empty page_title.

export function getPageTitle(pathname: string): string {
    const staticTitles: Record<string, string> = {
        "/": "routes",
        "/account": "my_account",
        "/workspaces": "workspaces",
        "/sources": "sources",
        "/connections": "connections",
        "/connections/new": "new_connection",
        // Legacy paths: they redirect to /connections, but a pageview can still
        // fire for them before the redirect settles.
        "/destinations": "destinations",
        "/destinations/new": "new_destination",
        "/routes/new": "new_route_select_provider",
    };
    if (staticTitles[pathname]) return staticTitles[pathname];

    const dynamicPatterns: [RegExp, string][] = [
        [/^\/routes\/new\/[^/]+\/configuredestinations\/newdestination$/, "new_route_new_destination"],
        [/^\/routes\/new\/[^/]+\/configuredestinations\/destination\/[^/]+\/configuration$/, "new_route_destination_configuration"],
        [/^\/routes\/new\/[^/]+\/configuredestinations\/destination\/[^/]+$/, "new_route_destination_details"],
        [/^\/routes\/new\/[^/]+\/configuredestinations$/, "new_route_select_destinations"],
        [/^\/routes\/new\/configureprovider\/[^/]+$/, "new_route_configure_provider"],
        // Flow-map selection lives in the path (account/flow + ids); collapse the
        // whole subtree to one title so ids never become analytics dimensions.
        [/^\/routes\/[^/]+\/configurations(\/.*)?$/, "route_configurations"],
        [/^\/routes\/[^/]+\/[^/]+\/newdestination$/, "route_new_destination"],
        [/^\/routes\/[^/]+\/[^/]+\/[^/]+\/configuration$/, "route_destination_configuration"],
        [/^\/routes\/[^/]+\/[^/]+\/[^/]+$/, "route_destination_details"],
        [/^\/routes\/[^/]+\/([^/]+)$/, "route_$1"],
        [/^\/routes\/[^/]+$/, "route_overview"],
        [/^\/connections\/[^/]+\/configuration$/, "connection_configuration"],
        [/^\/connections\/[^/]+\/routes$/, "connection_routes"],
        [/^\/connections\/[^/]+$/, "connection_details"],
        [/^\/destinations\/[^/]+\/configuration$/, "destination_configuration"],
        [/^\/destinations\/[^/]+$/, "destination_details"],
        [/^\/destination\/[^/]+\/configuration$/, "destination_configuration"],
        [/^\/destination\/[^/]+$/, "destination_details"],
        [/^\/sources\/destination\/[^/]+\/configuration$/, "source_destination_configuration"],
        [/^\/sources\/destination\/[^/]+$/, "source_destination_details"],
        [/^\/workspaces\/[^/]+$/, "workspace_details"],
    ];
    for (const [pattern, title] of dynamicPatterns) {
        const match = pathname.match(pattern);
        if (match) {
            if (title.includes("$1") && match[1]) {
                return title.replace("$1", match[1].toLowerCase());
            }
            return title;
        }
    }
    // Fallback: stable value to avoid high-cardinality from unmatched routes with IDs
    return "unknown_page";
}

function currentPage() {
    const pathname = typeof window !== "undefined" ? window.location.pathname : "";
    return { page_path: pathname, page_title: getPageTitle(pathname) };
}

// ── Helpers ──

// For event params: omits keys whose values are undefined, null, or "". Does not omit false or 0.
function cleanParams(params: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};
    const keys = Object.keys(params);
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const value = params[key];
        if (value !== undefined && value !== null && value !== "") {
            result[key] = value;
        }
    }
    return result;
}

// For user properties: every key is always sent. Falsy values become "not_available"
// so GA reports clearly show missing data instead of silent gaps.
function withDefaults(params: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};
    const keys = Object.keys(params);
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const value = params[key];
        result[key] = (value !== undefined && value !== null && value !== "") ? value : "not_available";
    }
    return result;
}

// ── Session setup (call once after login) ──
// Sets user properties: user_type, user_email, user_role, workspace_count,
// workspace_names, and language. Called once per session from App.tsx.

const USER_PROPERTIES_RETRY_DELAY_MS = 5 * 60 * 1000;
let _userPropertiesSet = false;
let _userPropertiesRequestInFlight = false;
let _nextUserPropertiesRetryAt = 0;

function getHighestRole(organizations: Array<{ role?: string }>): string {
    const roles = organizations.map(o => o.role).filter(Boolean);
    if (roles.includes("superuser")) return "superuser";
    if (roles.includes("admin")) return "admin";
    if (roles.includes("viewer")) return "viewer";
    return roles[0] || "unknown";
}

export async function setAnalyticsUserProperties(token: string) {
    if (_userPropertiesSet || _userPropertiesRequestInFlight || Date.now() < _nextUserPropertiesRetryAt) return;

    _userPropertiesRequestInFlight = true;
    try {
        const [user, organizations] = await Promise.all([
            getMyDetails(token),
            loadAllOrganizations(token).catch(() => []),
        ]);

        if (!user?.email) {
            _nextUserPropertiesRetryAt = Date.now() + USER_PROPERTIES_RETRY_DELAY_MS;
            return;
        }

        ReactGA.gtag("set", "user_properties", withDefaults({
            user_type: getUserType(user.email),
            user_domain: user.email.split("@")[1]?.toLowerCase(),
            user_role: user.is_superuser ? "superuser" : getHighestRole(organizations),
            workspace_count: organizations.length,
            workspace_names: organizations.map((o: any) => o.name).filter(Boolean).join(", "),
            language: i18next.language,
        }));

        _userPropertiesSet = true;
    } catch {
        _nextUserPropertiesRetryAt = Date.now() + USER_PROPERTIES_RETRY_DELAY_MS;
        // Silently fail — analytics should never break the app
    } finally {
        _userPropertiesRequestInFlight = false;
    }
}

// ── Pageview (SPA-aware) ──

export function trackPageView(path: string, title: string) {

    ReactGA.send({ hitType: "pageview", page: path, title });
}

// ── Route events ──

export function trackRouteCreationStarted(workspaceName?: string) {

    ReactGA.event("route_creation_started", cleanParams({
        ...currentPage(),
        workspace_name: workspaceName,
    }));
}

export function trackRouteCreated(routeId?: string, providerType?: string, workspaceName?: string) {

    ReactGA.event("route_created", cleanParams({
        ...currentPage(),
        route_id: routeId,
        provider_type: providerType,
        workspace_name: workspaceName,
    }));
}

export function trackRouteCreationAbandoned(lastStep: number, providerType?: string, workspaceName?: string) {

    ReactGA.event("route_creation_abandoned", cleanParams({
        ...currentPage(),
        last_step: lastStep,
        provider_type: providerType,
        workspace_name: workspaceName,
    }));
}

export function trackRouteEnabled(routeId?: string, providerType?: string, workspaceName?: string) {

    ReactGA.event("route_enabled", cleanParams({
        ...currentPage(),
        route_id: routeId,
        provider_type: providerType,
        workspace_name: workspaceName,
    }));
}

export function trackRouteDisabled(routeId?: string, providerType?: string, workspaceName?: string) {

    ReactGA.event("route_disabled", cleanParams({
        ...currentPage(),
        route_id: routeId,
        provider_type: providerType,
        workspace_name: workspaceName,
    }));
}

// ── Destination events ──

export function trackDestinationAdded(routeId?: string, destinationType?: string, workspaceName?: string) {

    ReactGA.event("destination_added", cleanParams({
        ...currentPage(),
        route_id: routeId,
        destination_type: destinationType,
        workspace_name: workspaceName,
    }));
}

export function trackDestinationUpdated(destinationId?: string, destinationType?: string, workspaceName?: string) {

    ReactGA.event("destination_updated", cleanParams({
        ...currentPage(),
        destination_id: destinationId,
        destination_type: destinationType,
        workspace_name: workspaceName,
    }));
}

// ── Destination creation flow ──

export function trackDestinationCreationStarted() {

    ReactGA.event("destination_creation_started", cleanParams({
        ...currentPage(),
    }));
}

export function trackDestinationCreationAbandoned(destinationType?: string, workspaceName?: string) {

    ReactGA.event("destination_creation_abandoned", cleanParams({
        ...currentPage(),
        destination_type: destinationType,
        workspace_name: workspaceName,
    }));
}

// ── Help panel ──

export function trackHelpPanelOpened() {

    ReactGA.event("help_panel_opened", cleanParams({
        ...currentPage(),
    }));
}

export function trackHelpArticleClicked(articleTitle: string, url: string) {

    ReactGA.event("help_article_clicked", cleanParams({
        ...currentPage(),
        article_title: articleTitle,
        url,
    }));
}

export function trackHelpSupportCenterClicked() {

    ReactGA.event("help_support_center_clicked", cleanParams({
        ...currentPage(),
    }));
}

export function trackHelpContactClicked() {

    ReactGA.event("help_contact_clicked", cleanParams({
        ...currentPage(),
    }));
}

// ── Account events ──

export function trackAccountProfileUpdated() {

    ReactGA.event("account_profile_updated", cleanParams({
        ...currentPage(),
    }));
}

export function trackAccountWorkspaceLeft(workspaceName?: string) {

    ReactGA.event("account_workspace_left", cleanParams({
        ...currentPage(),
        workspace_name: workspaceName,
    }));
}
